import type { ReactNode } from "react";
import { TiendaNubeIcon } from "../../../components/TiendaNubeIcon";

/**
 * Los logos de las marcas que aparecen en la pantalla de datos, DIBUJADOS a
 * mano como SVG inline.
 *
 * No hay assets de marca en el repo y no se pueden traer de un CDN (ni `<img>`
 * remoto ni `fetch`), así que cada uno es un `<path>` acá adentro: sin red, sin
 * archivo binario, sin ícono roto si algo no carga. Todos pintan con
 * `currentColor` — el color lo pone quien los usa (`COLOR_MARCA` para el estado
 * normal, gris para el apagado), así que el mismo dibujo sirve prendido y
 * apagado sin duplicar nada.
 *
 * Instagram, YouTube y TikTok son sus glifos oficiales redibujados; Meta es una
 * versión estilizada del lazo (la marca oficial es asimétrica, acá va la
 * simétrica, que a 20px se lee igual y no depende de un trazado que no podemos
 * copiar bien). Tiendanube NO se redibuja: delega en `components/TiendaNubeIcon`,
 * que ya tiene el trazado oficial y lo usan Mi marca y el historial — dos copias
 * del mismo path es la forma de que un día dejen de ser el mismo logo.
 */

export type MarcaLogo = "instagram" | "meta" | "tiendanube" | "youtube" | "tiktok";

interface PropsLogo {
  className?: string;
}

/** El color de cada marca, para el estado normal. En hex crudo: son colores de
 *  terceros, no del design system de Atlas, y no van en el theme. */
export const COLOR_MARCA: Record<MarcaLogo, string> = {
  instagram: "#E4405F",
  meta: "#0081FB",
  tiendanube: "#2C3357",
  youtube: "#FF0000",
  // El glifo de TikTok es negro; se usa el negro de Atlas para no meter un
  // segundo negro en la pantalla.
  tiktok: "#02062B", // hex-de-marca-ok: color de un tercero (TikTok)
};

export const NOMBRE_MARCA: Record<MarcaLogo, string> = {
  instagram: "Instagram",
  meta: "Meta",
  tiendanube: "Tiendanube",
  youtube: "YouTube",
  tiktok: "TikTok",
};

/** Cámara de contorno + lente + flash: el glifo de Instagram. */
export const LogoInstagram = ({ className }: PropsLogo) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
    <rect
      x="2.75"
      y="2.75"
      width="18.5"
      height="18.5"
      rx="5.25"
      stroke="currentColor"
      strokeWidth="1.9"
    />
    <circle cx="12" cy="12" r="4.35" stroke="currentColor" strokeWidth="1.9" />
    <circle cx="17.3" cy="6.7" r="1.25" fill="currentColor" />
  </svg>
);

/** El lazo de Meta: dos lóbulos que se cruzan en el centro. */
export const LogoMeta = ({ className }: PropsLogo) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
    <path
      d="M12 12c-2.2-3.6-3.9-5.6-6-5.6-2.25 0-3.85 2.5-3.85 5.6S3.75 17.6 6 17.6c2.1 0 3.8-2 6-5.6Zm0 0c2.2-3.6 3.9-5.6 6-5.6 2.25 0 3.85 2.5 3.85 5.6S20.25 17.6 18 17.6c-2.1 0-3.8-2-6-5.6Z"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * La nube de Tiendanube. Reusa el componente que ya existe en el repo en vez de
 * repetir el trazado oficial (ver el comentario de arriba). Su viewBox no es
 * cuadrado: con `preserveAspectRatio` por defecto se centra solo dentro de la
 * caja cuadrada que le da el marco.
 */
export const LogoTiendanube = ({ className }: PropsLogo) => (
  <TiendaNubeIcon className={className} />
);

/**
 * El botón de YouTube. Es UN solo `<path>` con `fillRule="evenodd"`: el
 * triángulo es un sub-trazado que queda AGUJEREADO, no un triángulo blanco
 * pintado encima. Así el logo funciona sobre cualquier fondo — con un triángulo
 * blanco se vería un parche sobre el gris del marco apagado.
 */
export const LogoYouTube = ({ className }: PropsLogo) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path
      fill="currentColor"
      fillRule="evenodd"
      d="M23.5 6.51a3.02 3.02 0 0 0-2.12-2.14C19.5 3.86 12 3.86 12 3.86s-7.5 0-9.38.51A3.02 3.02 0 0 0 .5 6.51C0 8.4 0 12 0 12s0 3.6.5 5.49a3.02 3.02 0 0 0 2.12 2.14c1.88.51 9.38.51 9.38.51s7.5 0 9.38-.51a3.02 3.02 0 0 0 2.12-2.14C24 15.6 24 12 24 12s0-3.6-.5-5.49ZM9.55 15.57V8.43L15.82 12l-6.27 3.57Z"
    />
  </svg>
);

/** La nota musical de TikTok. */
export const LogoTikTok = ({ className }: PropsLogo) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path
      fill="currentColor"
      d="M16.62 5.82a4.28 4.28 0 0 1-1.07-2.82h-3.19v12.4a2.59 2.59 0 0 1-2.59 2.5 2.6 2.6 0 0 1 0-5.19c.27 0 .53.04.78.12v-3.24a5.9 5.9 0 0 0-.78-.05 5.79 5.79 0 1 0 5.79 5.79V9.01a7.32 7.32 0 0 0 4.28 1.37V7.2a4.3 4.3 0 0 1-3.22-1.38Z"
    />
  </svg>
);

const POR_MARCA: Record<MarcaLogo, (props: PropsLogo) => ReactNode> = {
  instagram: LogoInstagram,
  meta: LogoMeta,
  tiendanube: LogoTiendanube,
  youtube: LogoYouTube,
  tiktok: LogoTikTok,
};

/**
 * Un logo dentro de su marco CIRCULAR. Es el único componente que usan las
 * pantallas: así el marco es exactamente igual en las tarjetas de conexión, en
 * la fila de plataformas y en cada fuente cargada.
 *
 * `apagado` lo pinta gris sobre fondo gris: es lo que usa Meta Ads, que no tiene
 * integración. Se ve distinto SIN texto de por medio.
 */
export const MarcoLogo = ({
  marca,
  tamano = "md",
  apagado = false,
}: {
  marca: MarcaLogo;
  tamano?: "sm" | "md";
  apagado?: boolean;
}) => {
  const Logo = POR_MARCA[marca];
  const caja = tamano === "sm" ? "h-8 w-8" : "h-11 w-11";
  const glifo = tamano === "sm" ? "h-4 w-4" : "h-[22px] w-[22px]";
  return (
    <span
      aria-hidden="true"
      className={`inline-flex ${caja} shrink-0 items-center justify-center rounded-full border border-[rgb(var(--brand-black-rgb)_/_0.08)] ${
        apagado ? "bg-[#F0F2F7]" : "bg-white"
      }`}
    >
      <span
        className={apagado ? "inline-flex text-[rgb(var(--brand-black-rgb)_/_0.3)]" : "inline-flex"}
        style={apagado ? undefined : { color: COLOR_MARCA[marca] }}
      >
        <Logo className={glifo} />
      </span>
    </span>
  );
};
